import type { RulebookDisplayedSnapshot } from "@next-platform/client-common-core";
import MeterMoveDirection from "@next-platform/client-common-core/rulebook/hud/MeterMoveDirection";
import resolveMeterMove from "@next-platform/client-common-core/rulebook/hud/resolveMeterMove";
import type { PublicMeterView } from "@next-platform/packages-ts-types";
import type { CSSProperties } from "react";
import { useMemo } from "react";

const DOWN_COLOR = "#E62C26";
const UP_TEXT_COLOR = "#17C964";
const FALLBACK_COLOR = "#AAAAAA";
/** Glow tint behind a meter that just moved: its color at 25%, for any CSS color (hex, named, rgb…). */
const TINT_PERCENT = 25;
const tintOf = (color: string): string => `color-mix(in srgb, ${color} ${TINT_PERCENT}%, transparent)`;

export type MeterMove = {
  delta: number;
  text: string;
  glowStyle: CSSProperties;
  floatStyle: CSSProperties;
  /** Animation class for the meter icon: a bump when it went up, a shake when it went down. */
  iconAnimation: string;
};

const resolveIconAnimation = (direction: MeterMoveDirection): string => {
  if (direction === MeterMoveDirection.Up) {
    return "rulebookHudBump";
  }
  return direction === MeterMoveDirection.Down ? "rulebookHudShake" : "";
};

const resolveTextColor = ({ direction, accent }: { direction: MeterMoveDirection; accent: string }): string => {
  if (direction === MeterMoveDirection.Roll) {
    return accent;
  }
  return direction === MeterMoveDirection.Down ? DOWN_COLOR : UP_TEXT_COLOR;
};

/** The glow, floating "+N" and icon animation for a meter whose snapshot just arrived; `null` when nothing plays. */
export default function useMeterMove({
  meter,
  color,
  snapshot,
  isArrived,
}: {
  meter: PublicMeterView;
  /** The meter's resolved palette hex (`useMeterColor`). */
  color: string | undefined;
  snapshot: RulebookDisplayedSnapshot;
  isArrived: boolean;
}): MeterMove | null {
  return useMemo(() => {
    const move = resolveMeterMove({ meter, snapshot, isArrived });
    if (!move) {
      return null;
    }
    const accent = color ?? FALLBACK_COLOR;
    const effectColor = move.direction === MeterMoveDirection.Down ? DOWN_COLOR : accent;
    return {
      delta: move.delta,
      text: move.text,
      glowStyle: { backgroundColor: tintOf(effectColor), "--rulebook-fx": effectColor } as CSSProperties,
      floatStyle: { color: resolveTextColor({ direction: move.direction, accent }) },
      iconAnimation: resolveIconAnimation(move.direction),
    };
  }, [isArrived, meter, color, snapshot]);
}
